import { Link, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import type { SharedProps } from '@/types'
import Avatar from '@/components/Avatar'
import { t } from '@/lib/i18n'

/** 页面对顶栏的配置。什么都不给 = 一级页（人 + 余额）；给 back/title = 二级页。 */
export type TopBarConfig = {
  /** 给了就是二级页：左边换成返回箭头，中间是标题。 */
  back?: string
  title?: string
  /** 右侧槽位（目标详情页的 ⋯）。二级页专用。 */
  right?: ReactNode
  /**
   * 二级页也显示人员切换与余额胶囊（作业页的稿子就是这个混合形态）。
   *
   * 为什么作业页要：换孩子看作业是每天都会做的动作（两个孩子年级不同、
   * 单子不同、上限不同），而余额是「做完能换什么」的另一半。
   * 其余二级页（目标详情、账号设置）不需要 —— 那些页只关于一个东西。
   */
  showPerson?: boolean
}

/**
 * 稿子的 `.topbar`：52px，sticky，只放两三样东西。
 *
 *   一级页：[人员 ▾]  ……  [💰 余额]
 *   二级页：[‹]  标题  ……  [右侧槽位]
 *
 * 挪走的四样与理由（差距审计 §5）：四个 tab → 底部 tab bar；📦 logo → 删掉
 * （稿子刻意清空顶栏，品牌在营销页和 PWA 图标上）；🔊 静音 → 「我的」sheet
 * （它是设置，不是每天要点的）；头像 dropdown → 两个 sheet（手机上够不着右上角）。
 * 👁 只读徽章**留下**：它是每一页都要成立的状态，藏进 sheet 等于让人点半天才发现写不了。
 */
export default function TopBar({ back, title, right, showPerson, onPerson }: TopBarConfig & { onPerson: () => void }) {
  const { current_person } = usePage<SharedProps>().props

  return (
    <header className="sticky top-0 z-30 flex items-center gap-2 h-13 px-3 bg-card border-b border-line">
      {back ? (
        <>
          {/* -ml-2.5 把 44px 的热区往外顶出去：视觉上箭头贴边，手感上还是 44px。 */}
          <Link
            href={back}
            aria-label={t('layout.back')}
            className="min-w-tap min-h-tap -ml-2.5 flex items-center justify-center text-xl text-muted"
          >
            ‹
          </Link>
          {showPerson ? (
            // 稿子 app-homework.html 的顶栏：‹ + 人员切换 + 余额胶囊。
            // 人名压到 7rem：这一行要放五样东西（返回 44 + 头像 28 + 人名 112
            // + 余额 ~66 + 右槽 44 ≈ 294px < 375px）。
            <button
              type="button"
              onClick={onPerson}
              aria-haspopup="dialog"
              className="flex items-center gap-[7px] min-h-tap px-1.5 rounded-full active:bg-bg"
            >
              <Avatar name={current_person?.name} url={current_person?.avatar_url} />
              <span className="font-semibold text-[15px] truncate max-w-[7rem]">
                {current_person?.name ?? t('layout.mine')}
              </span>
              <span className="text-faint text-[11px]">▾</span>
            </button>
          ) : (
            <h1 className="m-0 text-base font-bold truncate">{title}</h1>
          )}
          <span className="flex-1" />
          {showPerson && (
            <Link
              href="/store"
              aria-label={t('layout.balance_aria', { count: current_person?.balance ?? 0 })}
              className="flex items-center min-h-tap px-3 rounded-full bg-tint text-brand font-bold text-[13px] whitespace-nowrap active:scale-[.96]"
            >
              💰 {current_person?.balance ?? 0}
            </Link>
          )}
          {right}
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onPerson}
            aria-haspopup="dialog"
            className="flex items-center gap-[7px] min-h-tap px-1.5 rounded-full active:bg-bg"
          >
            <Avatar name={current_person?.name} url={current_person?.avatar_url} />
            <span className="font-semibold text-[15px] truncate max-w-[9rem]">
              {current_person?.name ?? t('layout.mine')}
            </span>
            <span className="text-faint text-[11px]">▾</span>
          </button>

          {/* 文案**不**藏在断点后面。旧顶栏塞了七样东西，只能把「只读」两个字
              hidden sm:inline 掉，于是手机上只剩一个光秃秃的 👁 —— 而手机正是
              这个状态最需要被看见的地方（点半天才发现写不了）。导航挪走之后
              这里放得下：人名最宽 9rem + 徽章 + 余额胶囊 ≈ 303px < 375px。 */}
          {current_person && !current_person.can_write && (
            <span
              title={t('layout.read_only_title', { name: current_person.name })}
              className="shrink-0 bg-amber-tint text-amber-strong font-semibold px-2 py-1 rounded-full text-xs whitespace-nowrap"
            >
              👁 {t('layout.read_only')}
            </span>
          )}

          <span className="flex-1" />

          {/* 余额胶囊本身就是去商店的入口（稿子里 balance 是个 <a>）：
              「我有多少分」和「能换什么」是同一个念头的两半。44px 高，不是装饰。 */}
          <Link
            href="/store"
            aria-label={t('layout.balance_aria', { count: current_person?.balance ?? 0 })}
            className="flex items-center min-h-tap px-3.5 rounded-full bg-tint text-brand font-bold text-sm whitespace-nowrap active:scale-[.96]"
          >
            💰 {current_person?.balance ?? 0}
          </Link>
        </>
      )}
    </header>
  )
}
